import { Cell, Edge, Graph } from '@antv/x6'
import { pick } from 'lodash-es'
import { ElMessage } from 'element-plus'
import { checkEdgeNameRepeat } from '@/hooks/Graph/utils'
import { getPositions } from '@/apis/objectManage'
import { getErpFiled } from '@/apis/flow'
import { moduleType, operateListEnum } from '@/enums/preObjectManage'
import { inComeCodes, loanCodes, billCodes, moneyLendingCodes } from '@/enums/flow'

const edgKey = ['from', 'id', 'to', 'name', 'condition', 'def', 'order']
const nodeKey = [
  'index',
  'flag',
  'name',
  'type',
  'notifyConfig',
  'timeoutConfig',
  'approvalType',
  'levelApprovalRange',
  'approver',
  'cc',
  'permissions',
  'actions',
  'approvalRule',
  'missingApproverRule',
  'tableCode',
  'qy',
  'appCode',
  'missingCandidates',
  'dataTitleMapping'
]
type MCell = Partial<Cell>
type MEdge = Partial<Edge>
interface Data {
  transitions: MEdge[]
  nodes: MCell[]
}

// 提交数据设置
export const serializeData = async (cells: (MCell | MEdge)[] = [], tableCode: string): Data => {
  const fieldList = await getFieldAuth(tableCode)
  return cells.reduce(
    (pre, nex) => {
      if (nex.shape === 'customer-edge') {
        const { source, target }: any = nex as Edge
        const edg = {
          ...nex,
          ...nex.data,
          from: source.cell,
          to: target.cell
        }
        pre.transitions = pre.transitions.concat(pick(edg, edgKey))
      } else {
        const permissions: Recordable = {}
        const node = {
          ...nex,
          ...nex.data,
          tableCode,
          index: nex.id
        }
        console.log(nex.data, 'nmd')
        if (node.type !== 'STRUCT') {
          for (const key in node?.permissions) {
            if (fieldList.find(item => item.columnSerial === key || item.fieldSerial === key)) {
              permissions[key] = node.permissions[key]
            }
          }
          node.permissions = permissions
          Object.assign(node, {})
        }
        pre.nodes = pre.nodes.concat(pick(node, nodeKey))
      }
      return pre
    },
    {
      transitions: [],
      nodes: []
    } as Data
  )
}

// 提交数据前校验
export const validate = (graph: Graph): boolean => {
  let valid = true
  const cells = graph.getCells()
  const rootNodes = graph.getRootNodes()
  const LeafNodes = graph.getLeafNodes()
  // 根节点和叶子节点只能有一个且为开始和结束节点
  if (rootNodes.length === 1 && rootNodes[0].id !== '0') {
    ElMessage.error('开始节点未连接下级节点！')
    valid = false
    return valid
  } else if (LeafNodes.length === 1 && LeafNodes[0].id !== '-1') {
    ElMessage.error('结束节点未连接上级节点！')
    valid = false
    return valid
  } else if (rootNodes.length > 1 || LeafNodes.length > 1) {
    ElMessage.error('存在未连接的节点，请连接！')
    valid = false
    return valid
  }
  if (checkEdgeNameRepeat(graph)) {
    ElMessage.error('线名称存在重复值，请修改！')
    valid = false
    return valid
  }
  cells.forEach(cell => {
    if (cell.shape === 'flow-approval-comp') {
      if (!nodeValidate(cell)) valid = false
    }
  })
  if (!valid) {
    ElMessage.error('节点存在未填写的属性，请按节点提示补全！')
  }
  return valid
}

// 判断业务节点必填
export const nodeValidate = (cell: MCell): boolean => {
  let valid = true
  const { data } = cell
  // 节点名称、审批类型、节点审批人、逐级审批规则、字段权限
  if (!data.name) {
    cell.setData!({ error: '节点名称未配置，请在属性中完成配置' })
    valid = false
    return valid
  }
  if (!data.approvalType) {
    cell.setData!({ error: '节点审批类型未配置，请在属性中完成配置' })
    valid = false
    return valid
  }
  if (data.approvalType === 'NORMAL' && !data?.approver?.length) {
    cell.setData!({ error: '节点处理人未配置，请在属性中完成配置' })
    valid = false
    return valid
  }
  if (data.approvalType === 'LEVEL' && !data.levelApprovalRange) {
    cell.setData!({ error: '逐级审批规则不能为空，请添加' })
    // data.error = '逐级审批规则不能为空，请添加'
    valid = false
    return valid
  }
  if (!data.permissions) {
    cell.setData!({ error: '字段权限未配置，请在属性中完成配置' })
    valid = false
    return valid
  } else {
    let allNull = true
    for (const key in data.permissions) {
      if (data.permissions[key].length) allNull = false
    }
    if (allNull) {
      cell.setData!({ error: '字段权限未配置，请在属性中完成配置' })
      valid = false
      return valid
    }
  }
  cell.setData!({ error: '' })
  return valid
}

// 获取字段权限
export async function getFieldAuth(tableCode: string) {
  const erpCodes = [...billCodes, ...moneyLendingCodes, ...inComeCodes, ...loanCodes]
  const fieldList: Recordable[] = []
  try {
    // 判断一下是走ERP还是走PAAS
    if (erpCodes.includes(tableCode)) {
      const res = await getErpFiled({ tableCode })
      res?.forEach((item: Recordable) => {
        const list = item.columnOperateList || []
        const must = list?.find((str: string) => str === 'MUST')
        const disabled = list?.find((str: string) => str === 'ADD_PAGE_EDIT')
        fieldList.push({
          ...item,
          columnSerial: res.fieldSerial,
          required: !!must,
          disabled: !disabled,
          disabledRequired: !must ? inComeCodes.includes(tableCode) : false
        })
      })
    } else {
      const { positions } = await getPositions({
        tableCode,
        type: 10,
        ignoreDataScope: true
      })
      if (positions.length) {
        // 关联表单的字段只能勾选查看
        positions.forEach((position: Recordable) => {
          position?.modules?.forEach((module: Recordable) => {
            module?.componentList?.forEach((component: Recordable) => {
              const list = component?.columnInfo?.columnOperateList
              const must = list?.find((item: Recordable) => item.code === operateListEnum.MUST)
              const disabled =
                list?.find((item: Recordable) => item.code === operateListEnum.ADD_PAGE_EDIT) ||
                module.moduleType === moduleType.subForm
              fieldList.push({
                ...component?.columnInfo,
                columnSerial: component?.columnSerial,
                required: must?.value,
                disabled: !disabled?.value
              })
            })
          })
          position?.children?.forEach((child: Recordable) => {
            child?.modules?.forEach((module: Recordable) => {
              module?.componentList?.forEach((component: Recordable) => {
                const must = component?.columnInfo?.columnOperateList?.find(
                  (item: Recordable) => item.code === operateListEnum.MUST
                )
                const disabled = component?.columnInfo?.columnOperateList?.find(
                  (item: Recordable) => item.code === operateListEnum.ADD_PAGE_EDIT
                )
                fieldList.push({
                  ...component?.columnInfo,
                  columnSerial: component?.columnSerial,
                  required: must?.value,
                  disabled: !disabled?.value
                })
              })
            })
          })
        })
      }
    }
    console.log(fieldList, '1111')
    return fieldList
  } catch (e) {
    console.log(e)
    return fieldList
  }
}

// 动态参数回显
export function formatDynamicData(sourceList: Recordable[]) {
  const params: any = {
    acquireCurrent: false,
    acquireCurrentFather: false,
    formMember: false,
    formMemberLeader: false,
    formMemberList: [],
    formMemberLeaderList: []
  }
  const list = sourceList?.filter((item: any) => item.type === 'DYNAMIC')
  list.forEach((item: any) => {
    if (item.dynamicOperator === 'CURRENT_USER') {
      params.acquireCurrent = true
    }
    if (item.dynamicOperator === 'CURRENT_MANAGER') {
      params.acquireCurrentFather = true
    }
    // USER: 选择字段的用户 MANAGER: 选择字段的用户主管
    if (item.dynamicOperator === 'USER') {
      params.formMember = true
      params.formMemberList.push(item.columnSerial)
    }
    if (item.dynamicOperator === 'MANAGER') {
      params.formMemberLeader = true
      params.formMemberLeaderList.push(item.columnSerial)
    }
  })
  return params
}

// 动态参获取值
export function changeDynamicParams(params: any, tableCode: string) {
  const list = []
  if (params.acquireCurrent) {
    list.push({ type: 'DYNAMIC', dynamicOperator: 'CURRENT_USER' })
  }
  if (params.acquireCurrentFather) {
    list.push({ type: 'DYNAMIC', dynamicOperator: 'CURRENT_MANAGER' })
  }
  if (params.formMemberList?.length) {
    params.formMemberList.forEach((el: any) => {
      list.push({
        type: 'DYNAMIC',
        dynamicOperator: 'USER',
        columnSerial: el,
        tableCode: tableCode
      })
    })
  }
  if (params.formMemberLeaderList?.length) {
    params.formMemberLeaderList.forEach((el: any) => {
      list.push({
        type: 'DYNAMIC',
        dynamicOperator: 'MANAGER',
        columnSerial: el,
        tableCode: tableCode
      })
    })
  }
  return list
}
